import type { BankStatementStatus } from '@sahab/contracts'
import { z } from 'zod'
import { isIsoDate } from '../../lib/dates'
import { PAGE_SIZES } from '../finance/search'

export { PAGE_SIZES }

const isoDate = z
  .string()
  .refine((v) => isIsoDate(v))
  .optional()
  .catch(undefined)

const SORT_FIELDS = ['periodEnd', 'periodStart', 'number'] as const
type SortField = (typeof SORT_FIELDS)[number]
export type BankStatementSort = SortField | `-${SortField}`

export function isBankStatementSort(value: string): value is BankStatementSort {
  return (SORT_FIELDS as readonly string[]).includes(value.startsWith('-') ? value.slice(1) : value)
}

export const BANK_STATEMENT_DEFAULT_SORT = '-periodEnd'

/** The statements list lives in the URL; an invalid value falls back to the default. */
export const bankStatementSearchSchema = z.object({
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof PAGE_SIZES)[number] => (PAGE_SIZES as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
  sort: z.string().refine(isBankStatementSort).optional().catch(undefined),
  q: z.string().trim().max(200).optional().catch(undefined),
  treasuryAccountId: z.uuid().optional().catch(undefined),
  status: z.enum(['IN_PROGRESS', 'COMPLETED']).optional().catch(undefined),
  from: isoDate,
  to: isoDate,
})
export type BankStatementSearch = z.infer<typeof bankStatementSearchSchema>

export interface BankStatementListParams {
  page: number
  pageSize: number
  sort: string
  q?: string | undefined
  treasuryAccountId?: string | undefined
  status?: BankStatementStatus | undefined
  from?: string | undefined
  to?: string | undefined
}

export function toBankStatementListQuery(search: BankStatementSearch): BankStatementListParams {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? PAGE_SIZES[0],
    sort: search.sort ?? BANK_STATEMENT_DEFAULT_SORT,
    q: search.q || undefined,
    treasuryAccountId: search.treasuryAccountId,
    status: search.status,
    from: search.from,
    to: search.to,
  }
}

/** `/bank-statements/new?treasuryAccountId=`: the account to start the statement for. */
export const newBankStatementSearchSchema = z.object({
  treasuryAccountId: z.uuid().optional().catch(undefined),
})

/** The statement page: the matching workspace or the reconciliation report. */
export const bankStatementPageSearchSchema = z.object({
  tab: z.enum(['reconcile', 'report', 'attachments']).optional().catch(undefined),
})
